import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  schema?: Record<string, any> | Record<string, any>[];
  path?: string;
}

const seoCache = new Map<string, any>();

export function SEO({ title, description, canonicalUrl, schema, path }: SEOProps) {
  const appUrl = import.meta.env.VITE_APP_URL || 'https://konig-rohrreinigung-klempner-notdienst.de';
  const fullCanonicalUrl = canonicalUrl ? `${appUrl}${canonicalUrl}` : appUrl;
  const currentPath = path || canonicalUrl || window.location.pathname;

  const [dbSeo, setDbSeo] = useState<{ title: string; description: string; schema_json: string } | null>(null);

  useEffect(() => {
    if (!currentPath) return;

    if (seoCache.has(currentPath)) {
      setDbSeo(seoCache.get(currentPath));
      return;
    }

    fetch(`/api/seo/page${currentPath}`)
      .then(res => {
        const contentType = res.headers.get('content-type');
        if (res.ok && contentType && contentType.includes('application/json')) {
          return res.json().catch(() => null);
        }
        return null;
      })
      .then(data => {
        if (data) {
          seoCache.set(currentPath, data);
          setDbSeo(data);
        }
      })
      .catch(() => {});
  }, [currentPath]);

  const rawTitle = dbSeo?.title || title;
  const finalTitle = rawTitle.includes('König') ? rawTitle : `${rawTitle} | König Rohrreinigung`;
  const finalDescription = dbSeo?.description || description;
  let finalSchema = schema;
  if (dbSeo?.schema_json) {
    try {
      finalSchema = JSON.parse(dbSeo.schema_json);
    } catch (e) {
      console.error('Failed to parse schema', e);
    }
  }

  return (
    <Helmet>
      <html lang="de" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="König Rohrreinigung & Klempner Notdienst" />
      <meta property="og:image" content={`${appUrl}/images/hero-home.webp`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${appUrl}/images/hero-home.webp`} />

      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

      {/* JSON-LD Schema */}
      {finalSchema && (
        <script type="application/ld+json">
          {JSON.stringify(finalSchema)}
        </script>
      )}
    </Helmet>
  );
}
